import React, {useEffect, useMemo, useRef, useState} from 'react';
import api from '@/services/api';
import {Player} from '@/types';
import FormationPitch, {PlayerAnnotation} from '@/components/team/FormationPitch';
import {generateFormation} from '@/data/formations';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingStatus from '@/components/ui/LoadingStatus';
import {useToast} from '@/context/ToastContext';
import {useWorkspace} from '@/context/WorkspaceContext';
import {
  draftStorageKey,
  PositionMap,
  SavedLineupV2,
  ScoutingAgg,
  TeamSelectionDraft,
  TeamSelectionPlayer,
  TeamSelectionPlayerPayload,
} from './TeamSelectionPage/types';
import SelectionToolbar from './TeamSelectionPage/SelectionToolbar';
import MatchBrowserSection from './TeamSelectionPage/MatchBrowserSection';
import PlayerTooltip from './TeamSelectionPage/PlayerTooltip';
import TeamAverages from './TeamSelectionPage/TeamAverages';
import SelectedPlayersBench from './TeamSelectionPage/SelectedPlayersBench';
import PlayerChooser from './TeamSelectionPage/PlayerChooser';
import {useGlobalDirtyGuard} from '@/context/DirtyNavigationProvider';

const emptyScouting: ScoutingAgg = {
  shooting: 0, passing: 0, pace: 0, dribbling: 0, defending: 0, physical: 0,
};

const mapPlayer = (player: TeamSelectionPlayerPayload): TeamSelectionPlayer => ({
  id: player.id,
  name: player.name,
  age: player.age,
  position: player.position,
  team: player.team || undefined,
  photoUrl: player.photo_url || undefined,
  injuryStatus: player.availability,
  latestPerformanceRating: player.latest_performance_rating,
  scouting: player.scouting,
});

const selectionSnapshot = (formationName: string, selected: PositionMap, benchIds: string[], unresolvedPositionIds: Record<string, string>, saveName: string, matchDate: string): string => JSON.stringify({
  formationName,
  positions: Object.fromEntries(Object.entries(selected).flatMap(([positionId, player]) => player ? [[positionId, player.id]] : []).sort(([left], [right]) => left.localeCompare(right))),
  benchIds: [...benchIds],
  unresolvedPositionIds: Object.fromEntries(Object.entries(unresolvedPositionIds).sort(([left], [right]) => left.localeCompare(right))),
  saveName,
  matchDate,
});

const readWorkspaceDraft = (workspaceId: string): TeamSelectionDraft | null => {
  try {
    const raw = localStorage.getItem(draftStorageKey(workspaceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TeamSelectionDraft>;
    if (parsed.workspaceId !== workspaceId || typeof parsed.formationName !== 'string') return null;
    return {
      workspaceId,
      formationName: parsed.formationName,
      selected: parsed.selected || {},
      benchIds: Array.isArray(parsed.benchIds) ? parsed.benchIds : [],
      unresolvedPositionIds: parsed.unresolvedPositionIds || {},
      saveName: typeof parsed.saveName === 'string' ? parsed.saveName : 'Matchday Squad',
      matchDate: typeof parsed.matchDate === 'string' ? parsed.matchDate : '',
      baseline: typeof parsed.baseline === 'string' ? parsed.baseline : undefined,
    };
  } catch {
    return null;
  }
};

const TeamSelectionWorkspace: React.FC<{workspaceId: string}> = ({workspaceId}) => {
  const {showToast} = useToast();
  const initialDraft = useMemo(() => readWorkspaceDraft(workspaceId), [workspaceId]);
  const [players, setPlayers] = useState<TeamSelectionPlayer[]>([]);
  const [playersState, setPlayersState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [query, setQuery] = useState('');
  const [formationName, setFormationName] = useState(initialDraft?.formationName || '4-3-3');
  const [selected, setSelected] = useState<PositionMap>(initialDraft?.selected || {});
  const [benchIds, setBenchIds] = useState<string[]>(initialDraft?.benchIds || []);
  const [unresolvedPositionIds, setUnresolvedPositionIds] = useState<Record<string, string>>(initialDraft?.unresolvedPositionIds || {});
  const [summaryTruncated, setSummaryTruncated] = useState(false);
  const [summaryAttempt, setSummaryAttempt] = useState(0);
  const [activePositionId, setActivePositionId] = useState<string | null>(null);
  const [hoverInfo, setHoverInfo] = useState<{player?: TeamSelectionPlayer; x: number; y: number} | null>(null);
  const [savedPerformance, setSavedPerformance] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState(initialDraft?.saveName || 'Matchday Squad');
  const [matchDate, setMatchDate] = useState(initialDraft?.matchDate || '');
  const [baseline, setBaseline] = useState(initialDraft?.baseline || selectionSnapshot('4-3-3', {}, [], {}, 'Matchday Squad', ''));
  const [savedTeams, setSavedTeams] = useState<SavedLineupV2[]>([]);
  const [savedPage, setSavedPage] = useState(1);
  const [savedTotalPages, setSavedTotalPages] = useState(0);
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const [loadingLineup, setLoadingLineup] = useState(false);
  const [lineupError, setLineupError] = useState('');
  const [showMatchBrowser, setShowMatchBrowser] = useState(false);
  const [pitchAnnotations, setPitchAnnotations] = useState<Record<string, PlayerAnnotation>>({});
  const lineupLoadGeneration = useRef(0);
  const lineupLoadController = useRef<AbortController | null>(null);
  const savedListGeneration = useRef(0);
  const savedPageRef = useRef(savedPage);
  const saveGeneration = useRef(0);
  const saveController = useRef<AbortController | null>(null);
  const selectedRef = useRef(selected);
  const benchIdsRef = useRef(benchIds);
  const unresolvedPositionIdsRef = useRef(unresolvedPositionIds);
  const formationNameRef = useRef(formationName);
  selectedRef.current = selected;
  benchIdsRef.current = benchIds;
  unresolvedPositionIdsRef.current = unresolvedPositionIds;
  formationNameRef.current = formationName;

  function invalidateLineupLoad(updateLoading = true) {
    lineupLoadGeneration.current += 1;
    lineupLoadController.current?.abort();
    lineupLoadController.current = null;
    if (updateLoading) setLoadingLineup(false);
  }

  function invalidateSave() {
    saveGeneration.current += 1;
    saveController.current?.abort();
    saveController.current = null;
  }

  const formation = useMemo(() => generateFormation(formationName), [formationName]);
  const currentSnapshot = useMemo(() => selectionSnapshot(formationName, selected, benchIds, unresolvedPositionIds, saveName, matchDate), [benchIds, formationName, matchDate, saveName, selected, unresolvedPositionIds]);
  const initialDraftSnapshot = useMemo(() => initialDraft && selectionSnapshot(
    initialDraft.formationName,
    initialDraft.selected,
    initialDraft.benchIds,
    initialDraft.unresolvedPositionIds || {},
    initialDraft.saveName || 'Matchday Squad',
    initialDraft.matchDate || '',
  ), [initialDraft]);
  useGlobalDirtyGuard(`team-selection-${workspaceId}`, currentSnapshot !== baseline, 'team selection');

  useEffect(() => {
    const controller = new AbortController();
    setPlayers([]);
    setPlayersState('loading');
    api.get('/v2/team-selection/players', {signal: controller.signal, headers: {'X-Workspace-ID': workspaceId}})
      .then(response => {
        if (controller.signal.aborted) return;
        const loadedPlayers: TeamSelectionPlayer[] = response.data.data.map(mapPlayer);
        const truncated = Boolean(response.data.meta?.truncated);
        const byID = new Map(loadedPlayers.map(player => [player.id, player]));
        const validPositions = new Set(generateFormation(formationNameRef.current).positions.map(position => position.id));
        const reconciledSelected: PositionMap = {};
        Object.entries(selectedRef.current).forEach(([positionID, player]) => {
          if (!player || !validPositions.has(positionID)) return;
          const loaded = byID.get(player.id);
          if (loaded) reconciledSelected[positionID] = loaded;
          else if (truncated) reconciledSelected[positionID] = player;
        });
        const occupiedIDs = new Set(Object.values(reconciledSelected).flatMap(player => player ? [player.id] : []));
        const reconciledUnresolved: Record<string, string> = {};
        const movedUnresolvedIDs: string[] = [];
        Object.entries(unresolvedPositionIdsRef.current).forEach(([positionID, playerID]) => {
          if (!validPositions.has(positionID)) {
            movedUnresolvedIDs.push(playerID);
            return;
          }
          const loaded = byID.get(playerID);
          if (loaded && !reconciledSelected[positionID] && !occupiedIDs.has(playerID)) {
            reconciledSelected[positionID] = loaded;
            occupiedIDs.add(playerID);
          } else if (truncated && !reconciledSelected[positionID] && !occupiedIDs.has(playerID)) {
            reconciledUnresolved[positionID] = playerID;
            occupiedIDs.add(playerID);
          }
        });
        const reconciledBench = [...benchIdsRef.current, ...movedUnresolvedIDs].filter((id, index, all) =>
          !occupiedIDs.has(id) && (truncated || byID.has(id)) && all.indexOf(id) === index);
        setPlayers(loadedPlayers);
        setSelected(reconciledSelected);
        setBenchIds(reconciledBench);
        setUnresolvedPositionIds(reconciledUnresolved);
        setSummaryTruncated(truncated);
        setPlayersState('ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setPlayers([]);
        setPlayersState('error');
        showToast('Unable to load squad players.', 'error');
      });
    return () => controller.abort();
  }, [showToast, summaryAttempt, workspaceId]);

  const loadSavedTeams = React.useCallback(async (page: number, signal?: AbortSignal) => {
    const generation = ++savedListGeneration.current;
    try {
      const response = await api.get(`/v2/lineups?page=${page}&page_size=100`, {signal, headers: {'X-Workspace-ID': workspaceId}});
      if (signal?.aborted || generation !== savedListGeneration.current) return;
      setSavedTeams(response.data.data);
      setSavedTotalPages(response.data.meta?.total_pages || 0);
    } catch {
      if (signal?.aborted || generation !== savedListGeneration.current) return;
      setSavedTeams([]);
      setSavedTotalPages(0);
      showToast('Unable to load saved lineups.', 'error');
    }
  }, [showToast, workspaceId]);

  useEffect(() => {
    const controller = new AbortController();
    setSavedTeams([]);
    void loadSavedTeams(savedPage, controller.signal);
    return () => controller.abort();
  }, [loadSavedTeams, savedPage, workspaceId]);

  useEffect(() => () => {
    invalidateLineupLoad(false);
    invalidateSave();
    savedListGeneration.current += 1;
  }, []);

  useEffect(() => {
    if (initialDraftSnapshot && playersState !== 'ready' && currentSnapshot === initialDraftSnapshot) return;
    try {
      const draft: TeamSelectionDraft = {workspaceId, formationName, selected, benchIds, unresolvedPositionIds, saveName, matchDate, baseline};
      localStorage.setItem(draftStorageKey(workspaceId), JSON.stringify(draft));
    } catch {
      // Draft persistence is best-effort; server persistence remains available.
    }
  }, [workspaceId, formationName, selected, benchIds, unresolvedPositionIds, saveName, matchDate, baseline, currentSnapshot, initialDraftSnapshot, playersState]);

  const unresolvedIDs = useMemo(() => {
    if (!summaryTruncated) return [];
    const knownIDs = new Set(players.map(player => player.id));
    return Array.from(new Set([
      ...Object.values(selected).flatMap(player => player && !knownIDs.has(player.id) ? [player.id] : []),
      ...Object.values(unresolvedPositionIds),
      ...benchIds.filter(id => !knownIDs.has(id)),
    ]));
  }, [benchIds, players, selected, summaryTruncated, unresolvedPositionIds]);
  const mutationsDisabled = playersState !== 'ready' || unresolvedIDs.length > 0;

  const roleToPlayerPosition = useMemo<Record<'GK' | 'DEF' | 'MID' | 'FWD', Player['position']>>(() => ({
    GK: 'Goalkeeper', DEF: 'Defender', MID: 'Midfielder', FWD: 'Forward',
  }), []);

  const availablePlayers = useMemo(() => {
    const usedIds = new Set(Object.values(selected).filter(Boolean).map(player => player!.id));
    return players.filter(player => !usedIds.has(player.id) && player.name.toLowerCase().includes(query.toLowerCase()));
  }, [players, query, selected]);

  const availablePlayersSorted = useMemo(() => {
    if (!activePositionId) return availablePlayers;
    const position = formation.positions.find(item => item.id === activePositionId);
    if (!position) return availablePlayers;
    const target = roleToPlayerPosition[position.role];
    return [...availablePlayers].sort((a, b) => {
      const positionOrder = Number(a.position !== target) - Number(b.position !== target);
      return positionOrder || a.name.localeCompare(b.name);
    });
  }, [activePositionId, availablePlayers, formation.positions, roleToPlayerPosition]);

  const teamAverages = useMemo(() => {
    const selectedPlayers = Object.values(selected).filter((player): player is TeamSelectionPlayer => Boolean(player));
    if (!selectedPlayers.length) return {performanceAvg: null, scouting: null, weakestKeys: [] as string[]};
    const ratings = selectedPlayers.map(player => player.latestPerformanceRating).filter(rating => rating > 0);
    const performanceAvg = ratings.length
      ? Math.round(ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length * 10) / 10
      : null;
    const withScouting = selectedPlayers.filter(player => Object.values(player.scouting).some(value => value > 0));
    if (!withScouting.length) return {performanceAvg, scouting: null, weakestKeys: [] as string[]};
    const scouting = withScouting.reduce((totals, player) => ({
      shooting: totals.shooting + player.scouting.shooting,
      passing: totals.passing + player.scouting.passing,
      pace: totals.pace + player.scouting.pace,
      dribbling: totals.dribbling + player.scouting.dribbling,
      defending: totals.defending + player.scouting.defending,
      physical: totals.physical + player.scouting.physical,
    }), {...emptyScouting});
    for (const key of Object.keys(scouting) as Array<keyof ScoutingAgg>) {
      scouting[key] = Math.round(scouting[key] / withScouting.length * 10) / 10;
    }
    const weakestKeys = Object.entries(scouting).sort((a, b) => a[1] - b[1]).slice(0, 2).map(([key]) => key);
    return {performanceAvg, scouting, weakestKeys};
  }, [selected]);

  const clearSelection = () => {
    setSelected({});
    setBenchIds([]);
    setUnresolvedPositionIds({});
    setSavedPerformance({});
    setPitchAnnotations({});
    setBaseline(selectionSnapshot(formationName, {}, [], {}, saveName, matchDate));
  };

  const handleFormationChange = (name: string) => {
    const validPositions = new Set(generateFormation(name).positions.map(position => position.id));
    const retained: PositionMap = {};
    const movedIDs: string[] = [];
    Object.entries(selected).forEach(([positionID, player]) => {
      if (!player) return;
      if (validPositions.has(positionID)) retained[positionID] = player;
      else movedIDs.push(player.id);
    });
    const retainedUnresolved: Record<string, string> = {};
    Object.entries(unresolvedPositionIds).forEach(([positionID, playerID]) => {
      if (validPositions.has(positionID)) retainedUnresolved[positionID] = playerID;
      else movedIDs.push(playerID);
    });
    const retainedPitchIDs = new Set([
      ...Object.values(retained).flatMap(player => player ? [player.id] : []),
      ...Object.values(retainedUnresolved),
    ]);
    setSelected(retained);
    setUnresolvedPositionIds(retainedUnresolved);
    setBenchIds(previous => [...previous, ...movedIDs].filter((id, index, all) =>
      !retainedPitchIDs.has(id) && all.indexOf(id) === index));
    setFormationName(name);
    setActivePositionId(null);
  };

  const addToBench = (player: TeamSelectionPlayer) => {
    setSelected(previous => Object.fromEntries(Object.entries(previous).filter(([, selectedPlayer]) => selectedPlayer?.id !== player.id)));
    setBenchIds(previous => previous.includes(player.id) ? previous : [...previous, player.id]);
  };

  const handleChoose = (player: TeamSelectionPlayer) => {
    if (!activePositionId) return;
    setBenchIds(previous => previous.filter(id => id !== player.id));
    setSelected(previous => ({...previous, [activePositionId]: player}));
    setActivePositionId(null);
    setQuery('');
  };

  const handleLoadSaved = async (id: string) => {
    invalidateLineupLoad();
    setSelectedSavedId(id);
    if (!id) return;
    if (playersState !== 'ready') return;
    const controller = new AbortController();
    lineupLoadController.current = controller;
    const generation = ++lineupLoadGeneration.current;
    setLoadingLineup(true);
    setLineupError('');
    try {
      const response = await api.get(`/v2/lineups/${id}`, {signal: controller.signal, headers: {'X-Workspace-ID': workspaceId}});
      if (controller.signal.aborted || generation !== lineupLoadGeneration.current) return;
      const lineup: SavedLineupV2 = response.data.data;
      const positionMap: PositionMap = {};
      const validPositions = new Set(generateFormation(lineup.formation).positions.map(position => position.id));
      const selectedIDs = new Set<string>();
      const unresolvedPositions: Record<string, string> = {};
      Object.entries(lineup.positions).forEach(([positionID, playerID]) => {
        const player = players.find(candidate => candidate.id === playerID);
        if (player && validPositions.has(positionID) && !selectedIDs.has(playerID)) {
          positionMap[positionID] = player;
          selectedIDs.add(playerID);
        } else if (summaryTruncated && validPositions.has(positionID)) {
          unresolvedPositions[positionID] = playerID;
        }
      });
      setFormationName(lineup.formation);
      setSelected(positionMap);
      setUnresolvedPositionIds(unresolvedPositions);
      const loadedBenchIds = lineup.bench_ids.filter((playerID, index, all) =>
        (summaryTruncated || players.some(player => player.id === playerID)) && !selectedIDs.has(playerID) && all.indexOf(playerID) === index);
      const loadedName = lineup.name || 'Matchday Squad';
      const loadedDate = lineup.match_date || '';
      setBenchIds(loadedBenchIds);
      setSaveName(loadedName);
      setMatchDate(loadedDate);
      setSavedPerformance(lineup.saved_performance || {});
      setBaseline(selectionSnapshot(lineup.formation, positionMap, loadedBenchIds, unresolvedPositions, loadedName, loadedDate));
    } catch {
      if (controller.signal.aborted || generation !== lineupLoadGeneration.current) return;
      setLineupError('Unable to load lineup.');
      showToast('Unable to load lineup.', 'error');
    } finally {
      if (generation === lineupLoadGeneration.current) setLoadingLineup(false);
    }
  };

  const handleDeleteSaved = async (id: string) => {
    const lineup = savedTeams.find(item => item.id === id);
    invalidateLineupLoad();
    try {
      await api.delete(`/v2/lineups/${id}`, {headers: {'X-Workspace-ID': workspaceId}});
      setSavedTeams(previous => previous.filter(item => item.id !== id));
      if (selectedSavedId === id) setSelectedSavedId('');
      showToast(`${lineup?.name || 'Lineup'} deleted.`, 'success');
    } catch {
      showToast('Unable to delete lineup.', 'error');
    }
  };

  const handleSave = async () => {
    if (mutationsDisabled) return;
    saveController.current?.abort();
    const controller = new AbortController();
    saveController.current = controller;
    const generation = ++saveGeneration.current;
    const validPositions = new Set(formation.positions.map(position => position.id));
    const knownPlayers = new Set(players.map(player => player.id));
    const positions = Object.fromEntries(Object.entries(selected)
      .filter(([positionID, player]) => validPositions.has(positionID) && player && knownPlayers.has(player.id))
      .map(([id, player]) => [id, player!.id]));
    const safeBenchIDs = benchIds.filter(id => knownPlayers.has(id) && !Object.values(positions).includes(id));
    const selectedAndBench = new Set([...Object.values(positions), ...safeBenchIDs]);
    const snapshot = Object.fromEntries(players
      .filter(player => selectedAndBench.has(player.id) && player.latestPerformanceRating > 0)
      .map(player => [player.id, player.latestPerformanceRating]));
    setSaving(true);
    try {
      await api.post('/v2/lineups', {
        name: saveName || 'Matchday Squad', formation: formationName, positions, bench_ids: safeBenchIDs,
        match_date: matchDate || undefined, saved_performance: snapshot,
      }, {signal: controller.signal, headers: {'X-Workspace-ID': workspaceId}});
      if (controller.signal.aborted || generation !== saveGeneration.current) return;
      const savedName = saveName || 'Matchday Squad';
      if (!saveName) setSaveName(savedName);
      setBenchIds(safeBenchIDs);
      setBaseline(selectionSnapshot(formationName, selected, safeBenchIDs, unresolvedPositionIds, savedName, matchDate));
      showToast('Lineup saved.', 'success');
      await loadSavedTeams(savedPageRef.current);
    } catch {
      if (controller.signal.aborted || generation !== saveGeneration.current) return;
      showToast('Unable to save lineup.', 'error');
    } finally {
      if (!controller.signal.aborted && generation === saveGeneration.current) {
        saveController.current = null;
        setSaving(false);
      }
    }
  };

  const handleMatchLoaded: React.ComponentProps<typeof MatchBrowserSection>['onMatchLoaded'] = result => {
    setFormationName(result.formationName);
    setPitchAnnotations(result.annotations || {});
    const knownPlayers = new Map(players.map(player => [player.id, player]));
    const positionMap: PositionMap = {};
    Object.entries(result.positionMap).forEach(([positionID, player]) => {
      const known = player && knownPlayers.get(player.id);
      if (known) positionMap[positionID] = known;
    });
    const loadedBenchIds = result.benchPlayers.filter(player => knownPlayers.has(player.id)).map(player => player.id);
    const loadedName = result.matchLabel || 'Matchday Squad';
    const loadedDate = result.matchDate ? result.matchDate.slice(0, 10) : '';
    setSelected(positionMap);
    setUnresolvedPositionIds({});
    setBenchIds(loadedBenchIds);
    setSaveName(loadedName);
    setMatchDate(loadedDate);
    setBaseline(selectionSnapshot(result.formationName, positionMap, loadedBenchIds, {}, loadedName, loadedDate));
    setActivePositionId(null);
  };

  const pitchDescriptions = Object.fromEntries(Object.entries(selected).flatMap(([positionID, player]) => player ? [[
    positionID,
    `${player.name}. Current performance rating ${player.latestPerformanceRating || 'not available'}. Shooting ${player.scouting.shooting}, passing ${player.scouting.passing}, pace ${player.scouting.pace}, dribbling ${player.scouting.dribbling}, defending ${player.scouting.defending}, physical ${player.scouting.physical}.`,
  ]] : []));

  return (
    <div className="space-y-6">
      <SelectionToolbar
        formationName={formationName} onFormationChange={handleFormationChange}
        onClear={clearSelection} saveName={saveName} onSaveNameChange={setSaveName}
        matchDate={matchDate} onMatchDateChange={setMatchDate} saving={saving} onSave={handleSave}
        savedTeams={savedTeams} selectedSavedId={selectedSavedId} onLoadSaved={handleLoadSaved}
        onDeleteSaved={handleDeleteSaved}
        playersReady={playersState === 'ready'} loadingLineup={loadingLineup} lineupError={lineupError}
        mutationsDisabled={mutationsDisabled}
        savedPage={savedPage} savedTotalPages={savedTotalPages} onSavedPageChange={page => {
          invalidateLineupLoad();
          savedPageRef.current = page;
          setSelectedSavedId('');
          setSavedPage(page);
        }}
      />
      {playersState === 'loading' && <LoadingStatus label="Loading squad players" />}
      {playersState === 'error' && (
        <div role="alert" aria-label="Squad summary error">
          Unable to load squad players.
          <Button variant="outline" aria-label="Retry squad summary" onClick={() => setSummaryAttempt(attempt => attempt + 1)}>Retry</Button>
        </div>
      )}
      {unresolvedIDs.length > 0 && (
        <div role="alert" aria-label="Unresolved lineup references">
          Some lineup references could not be resolved from the truncated squad summary: {unresolvedIDs.join(', ')}. Saving and loading are disabled.
        </div>
      )}
      <MatchBrowserSection showMatchBrowser={showMatchBrowser} onToggle={() => setShowMatchBrowser(value => !value)} onMatchLoaded={handleMatchLoaded} />
      <Card>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <FormationPitch
              formation={formation} selected={selected} annotations={pitchAnnotations}
              descriptions={pitchDescriptions}
              onClickPosition={position => setActivePositionId(position.id)}
              onHoverPosition={(_, player, anchor) => setHoverInfo(player ? {player: player as TeamSelectionPlayer, x: anchor.clientX, y: anchor.clientY} : null)}
              onLeavePosition={() => setHoverInfo(null)}
              onClearPosition={positionID => setSelected(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => id !== positionID)))}
            />
            {hoverInfo?.player && <PlayerTooltip hoverInfo={hoverInfo} savedPerformance={savedPerformance} />}
            <TeamAverages teamAverages={teamAverages} />
            <SelectedPlayersBench
              formation={formation} selected={selected} players={players} benchIds={benchIds}
              onRemoveFromPosition={positionID => setSelected(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => id !== positionID)))}
              onAddToBench={addToBench} onRemoveFromBench={id => setBenchIds(previous => previous.filter(playerID => playerID !== id))}
            />
          </div>
          <PlayerChooser
            activePositionId={activePositionId} query={query} onQueryChange={setQuery}
            availablePlayersSorted={availablePlayersSorted} onChoose={handleChoose} onAddToBench={addToBench}
          />
        </div>
      </Card>
    </div>
  );
};

const TeamSelectionPage: React.FC = () => {
  const {currentWorkspace} = useWorkspace();
  if (!currentWorkspace?.id) return <div role="status">Select a workspace to manage lineups.</div>;
  return <TeamSelectionWorkspace key={currentWorkspace.id} workspaceId={currentWorkspace.id} />;
};

export default TeamSelectionPage;
